import { useState, useRef, useEffect, useCallback } from "react";
import { FiChevronDown, FiTrash2, FiFileText } from "react-icons/fi";
import { MdOutlineFileDownload, MdOutlineFileUpload } from "react-icons/md";
import {
  deleteInternationalDocument,
  uploadInternationalDocument,
} from "../../../../services/shipments.services";
import Confirm from "../../../../Components/Popups/Confirm";
import UploadDocumentModal from "./UploadDocumentModal";
import { showSnackBar } from "../../../../store/common/snackBarSlice";
import { useDispatch } from "react-redux";
import { useTranslation } from "react-i18next";

const DOCUMENT_TYPE_MAP = {
  "001": "Authorization_Form",
  "002": "Commercial_Invoice",
  "003": "Certificate_of_Origin",
  "004": "Export_Accompanying_Document",
  "005": "Export_License",
  "006": "Import_Permit",
  "007": "One_Time_USMCA",
  "008": "Other_Document",
  "009": "Power_of_Attorney",
  "010": "Packing_List",
  "011": "SED_Document",
  "012": "Shippers_Letter_of_Instruction",
  "013": "Declaration",
};

export default function InvoiceActionsDropdown({
  documents = [],
  shipmentId,
  onDownloadDeclaration,
  onDownloadAllZip,
  onDocumentsUpdate,
  onDocumentDelete,
}) {
  const [open, setOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleteLoader, setDeleteLoader] = useState(false);
  const [uploadModalOpen, setUploadModalOpen] = useState(false);
  const [uploadLoader, setUploadLoader] = useState(false);

  const dropdownRef = useRef(null);
  const dispatch = useDispatch();
  const { t } = useTranslation("finances");

  // ✅ FIX: prevent closing dropdown if ANY modal is open
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (!dropdownRef.current?.contains(e.target)) {
        if (!deleteTarget && !uploadModalOpen) {
          setOpen(false);
        }
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [deleteTarget, uploadModalOpen]);

  // ✅ File name generator
  const getFileName = useCallback((doc) => {
    try {
      const url = doc?.file || "";
      const file = url.split("/").pop()?.split("?")[0] || "";
      const ext = file.split(".").pop()?.toLowerCase() || "";

      const base = DOCUMENT_TYPE_MAP[doc?.document_type] || "Document";

      return `${base}_${doc?.type || "file"}${ext ? `.${ext}` : ""}`;
    } catch {
      return "file";
    }
  }, []);

  // ✅ Download handler
  const downloadDoc = useCallback(
    async (doc) => {
      try {
        const res = await fetch(doc.file);
        const blob = await res.blob();

        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");

        a.href = url;
        a.download = getFileName(doc);
        document.body.appendChild(a);
        a.click();
        a.remove();

        window.URL.revokeObjectURL(url);
      } catch {
        dispatch(
          showSnackBar({
            text: t("invoice.documents.downloadFailed"),
            severity: "error",
          }),
        );
      }
    },
    [getFileName, dispatch, t],
  );

  // ✅ Upload handler
  const handleUpload = async (uploadData) => {
    if (!uploadData.file || !uploadData.document_type) {
      dispatch(
        showSnackBar({
          text: t("invoice.documents.required"),
          severity: "error",
        }),
      );
      return;
    }

    try {
      setUploadLoader(true);

      const formData = new FormData();
      formData.append("document", uploadData.file);
      formData.append("document_type", uploadData.document_type);

      const res = await uploadInternationalDocument(shipmentId, formData);

      if (!res?.error) {
        onDocumentsUpdate(res?.data?.result);
        dispatch(
          showSnackBar({
            text: t("invoice.documents.uploaded"),
            severity: "success",
          }),
        );

        setUploadModalOpen(false);
      } else {
        throw new Error();
      }
    } catch {
      dispatch(
        showSnackBar({
          text: t("invoice.documents.uploadFailed"),
          severity: "error",
        }),
      );
    } finally {
      setUploadLoader(false);
    }
  };

  // ✅ Delete handler
  const deleteDocument = useCallback(async () => {
    if (!deleteTarget) return;

    try {
      setDeleteLoader(true);

      const res = await deleteInternationalDocument(
        deleteTarget.shipping_id,
        deleteTarget.id,
      );

      if (!res?.error) {
        onDocumentDelete(deleteTarget.id);

        dispatch(
          showSnackBar({
            text: t("invoice.documents.deleted"),
            severity: "success",
          }),
        );
      } else {
        throw new Error();
      }
    } catch {
      dispatch(
        showSnackBar({
          text: t("invoice.documents.deleteFailed"),
          severity: "error",
        }),
      );
    } finally {
      setDeleteLoader(false);
      setDeleteTarget(null);
    }
  }, [deleteTarget, onDocumentDelete, dispatch, t]);

  return (
    <>
      {/* ✅ Confirm Modal */}
      <Confirm
        open={!!deleteTarget}
        setOpen={(v) => !v && setDeleteTarget(null)}
        handleConfirm={deleteDocument}
        text={t("invoice.documents.deleteConfirm")}
        loader={deleteLoader}
      />

      {/* ✅ Upload Modal */}
      <UploadDocumentModal
        open={uploadModalOpen}
        setOpen={setUploadModalOpen}
        onUpload={handleUpload}
        uploadLoader={uploadLoader}
        DOCUMENT_TYPE_MAP={DOCUMENT_TYPE_MAP}
      />

      <div className="relative inline-block" ref={dropdownRef}>
        {/* Trigger */}
        <button
          onClick={() => setOpen((p) => !p)}
          className="flex items-center gap-1.5 rounded-md border border-ui-border bg-ui-surface px-2.5 py-1.5 text-xs font-medium text-ui-text hover:bg-ui-surface-sunken sm:gap-2 sm:px-3 sm:py-2 sm:text-sm sm:leading-none"
        >
          <span>{t("invoice.buttons.declaration")}</span>
          <FiChevronDown className="h-4 w-4 sm:h-5 sm:w-5" />
        </button>
        <span className="absolute -top-2 -right-1 rounded-full bg-brand text-white text-[9px] px-1.5 py-[1px] font-semibold shadow">
          {t("invoice.documents.newBadge")}
        </span>
        {/* <span className="absolute top-1 right-1 flex h-2 w-2">
          <span className="absolute inline-flex h-full w-full rounded-full bg-brand opacity-75 animate-ping"></span>
          <span className="relative inline-flex h-2 w-2 rounded-full bg-brand"></span>
        </span> */}
        {/* Dropdown */}
        {open && (
          <div className="absolute right-0 mt-2 w-72 bg-ui-surface border rounded-xl shadow-lg z-50">
            {/* Upload */}

            <button
              onClick={(e) => {
                e.stopPropagation();
                setUploadModalOpen(true);
              }}
              className="
                w-full flex items-center gap-2
                px-4 py-2 text-sm
                text-brand font-medium
                rounded-t-xl
                hover:bg-brand-light
                hover:text-brand-hover
                transition-colors duration-150
              "
            >
              <MdOutlineFileUpload className="text-base" />
              {t("invoice.documents.upload")}
            </button>

            <div className="border-t" />

            {/* Declaration download */}
            <button
              onClick={() => onDownloadDeclaration()}
              className="
                w-full flex items-center gap-2
                px-4 py-2 text-sm
                text-ui-text
                hover:bg-ui-surface-sunken
                hover:text-ui-text
                transition-colors duration-150
              "
            >
              <MdOutlineFileDownload className="text-base text-ui-text-muted" />
              {t("invoice.buttons.declaration")}
            </button>

            <div className="border-t" />

            {/* Documents */}
            <div className="px-4 py-2">
              <div className="text-xs font-semibold text-ui-text-muted mb-2">
                {t("invoice.documents.uploadedDocuments")}
              </div>

              {documents.length === 0 ? (
                <div className="text-xs text-ui-text-subtle text-center py-2">
                  {t("invoice.documents.none")}
                </div>
              ) : (
                <div className="space-y-1 max-h-40 overflow-y-auto">
                  {documents.map((doc) => (
                    <div
                      key={doc.id}
                      className="flex justify-between items-center bg-ui-surface-sunken px-2 py-1 rounded"
                    >
                      <div className="flex items-center gap-2 truncate">
                        <FiFileText size={14} />
                        <span className="text-xs truncate">
                          {getFileName(doc)}
                        </span>
                      </div>

                      <div className="flex gap-1">
                        <button onClick={() => downloadDoc(doc)}>
                          <MdOutlineFileDownload size={14} />
                        </button>

                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            setDeleteTarget(doc);
                          }}
                        >
                          <FiTrash2 size={14} className="text-state-danger" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* <div className="border-t mt-2" />

              <button
                onClick={() => {
                  onDownloadAllZip?.();
                  setOpen(false);
                }}
                className="w-full mt-2 flex justify-center gap-2 px-3 py-2 text-xs bg-[var(--color-brand)] text-white rounded-md"
              >
                <MdOutlineFileDownload size={14} />
                Download All (ZIP)
              </button> */}
            </div>
          </div>
        )}
      </div>
    </>
  );
}
